import { Link } from "react-router-dom";
import { FileDown } from "lucide-react";
import Seo from "@/components/site/Seo";
import EnquiryForm from "@/components/site/EnquiryForm";
import { Notice, PageHero, Reveal, SectionHeading } from "@/components/site/kit";
import useFetch from "@/hooks/useFetch";
import { fileUrl } from "@/lib/api";
import { CONSENT, SPONSOR_FIELDS } from "@/lib/forms";

const OPPS = [
  ["Title Sponsorship", "League naming rights and the most prominent presence across all league assets."],
  ["Associate Sponsorship", "Premium visibility across matchdays, digital platforms and league communications."],
  ["Team Sponsorship", "Partner with an individual franchise and its city, players and fans."],
  ["Official Partners", "Category-exclusive partnerships across products and services."],
  ["Venue Branding", "On-ground branding opportunities at the Hyderabad and Amaravati venues."],
  ["Digital Partnerships", "Social, content and fan-engagement partnerships across digital channels."],
  ["Broadcast-linked Sponsorship", "On-screen opportunities, available only subject to a confirmed media agreement."],
];

export default function Sponsors() {
  const { data: sponsors } = useFetch("/sponsors", []);
  return (
    <>
      <Seo title="Sponsorship" description="Sponsorship and partnership opportunities with the Telangana–Andhra T10 League: title, associate, team, official, venue, digital and broadcast-linked." />
      <PageHero testid="sponsors-hero" eyebrow="Sponsors & Partnerships" title="Partner With Two States of Cricket" image="/images/hero.jpg">
        Connect your brand with a new regional T10 property built for speed, local pride and digital audiences.
      </PageHero>
      <section className="container-x py-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {OPPS.map(([t, d], i) => (
            <Reveal key={t} delay={(i % 4) * 0.05} className={i === 0 ? "sm:col-span-2" : ""}>
              <div data-testid={`sponsor-opp-${i}`} className="card-lift h-full rounded-md border border-white/10 bg-[#112240] p-6">
                <span className="font-score text-xs text-[#FF5722]">0{i + 1}</span>
                <h2 className="mt-4 font-display text-2xl uppercase">{t}</h2>
                <p className="mt-2 text-sm text-slate-400">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10"><Notice>No sponsors have been confirmed yet. Broadcast-linked sponsorship is subject to a confirmed media agreement.</Notice></div>
      </section>
      {sponsors?.length > 0 && (
        <section className="container-x pb-20">
          <SectionHeading eyebrow="Our Partners" title="Official Sponsors" />
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {sponsors.map((s) => (
              <div key={s.id} data-testid={`sponsor-${s.id}`} className="flex flex-col items-center gap-3 rounded-md border border-white/10 bg-[#112240] p-6">
                {s.logo && <img src={fileUrl(s.logo)} alt={`${s.name} logo`} loading="lazy" className="h-16 object-contain" />}
                <p className="text-sm font-bold">{s.name}</p><p className="text-[10px] uppercase tracking-widest text-slate-400">{s.tier}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="bg-[#071325] py-20">
        <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <SectionHeading eyebrow="Get the Deck" title="Request the Sponsorship Deck">
              Select "Request Sponsorship Deck" as your interest in the form and our partnerships team will share the deck with you.
            </SectionHeading>
            <Link to="#enquiry" data-testid="request-deck-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#ff7a4f]">
              <FileDown className="h-4 w-4" /> Request Sponsorship Deck
            </Link>
          </div>
          <div id="enquiry" className="scroll-mt-28">
            <EnquiryForm testid="sponsor-form" fields={SPONSOR_FIELDS} endpoint="/forms/sponsor" submitLabel="Send Sponsorship Enquiry" consentText={CONSENT}
              success="Thank you for your interest in partnering with the Telangana–Andhra T10 League. Our partnerships team will contact you shortly." />
          </div>
        </div>
      </section>
    </>
  );
}
